import InquiryApi from '@apis/inquiry/InquiryApi';
import useToast from '@hooks/ui/useToast';
import { ErrorResponse } from '@models/error';
import { CreateInquiryRequest } from '@models/inquiry';
import { useFlow, useStepFlow } from '@stackflow/react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function useInquiryForm() {
    const { t } = useTranslation('inquiry');
    const [inquiryTypeId, setInquiryTypeId] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [content, setContent] = useState<string>('');
    const [fileUrls, setFileUrls] = useState<string[]>([]);
    const toast = useToast();
    const [privacy, setPrivacy] = useState<boolean>(false);
    const { replaceStep } = useStepFlow('InquiryActivity');
    const { push } = useFlow();

    const reset = () => {
        setInquiryTypeId('');
        setTitle('');
        setContent('');
        setFileUrls([]);
        setPrivacy(false);
    };

    const { mutate } = useMutation({
        mutationFn: InquiryApi.createInquiry,
        onSuccess(data) {
            toast.success(t('toast.success'));
            reset();
            const { inquiryId } = data;
            replaceStep({ type: 'READ' });
            push('InquiryLogActivity', { inquiryId });
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data.message || t('toast.error'));
        },
    });

    const handleCreateInquiry = () => {
        if (!inquiryTypeId) {
            toast.error(t('toast.typeRequired'));
            return;
        }
        if (!title) {
            toast.error(t('toast.titleRequired'));
            return;
        }
        if (!content) {
            toast.error(t('toast.contentRequired'));
            return;
        }
        if (!privacy) {
            toast.error(t('toast.privacyRequired'));
            return;
        }
        const request: CreateInquiryRequest = {
            inquiryTypeId, title, content, fileUrls,
        };
        mutate(request);
    };
    return {
        inquiryTypeId,
        setInquiryTypeId,
        title,
        setTitle,
        content,
        setContent,
        fileUrls,
        setFileUrls,
        handleCreateInquiry,
        reset,
        privacy,
        setPrivacy,
    };
}

export default useInquiryForm;
